import { Helmet } from 'react-helmet-async';

// v26.14 — Dual-domain SEO
// Two live domains serve the same app:
//   • durabledesigns.co.uk  — global / default (x-default + en-gb)
//   • durabledesigns.asia   — Asia (en-hk)
// SEO.jsx emits a canonical pointing at the *current* domain (so each domain
// keeps its own index) and hreflang alternates pointing at both, so Google
// knows they're equivalents and won't flag as duplicate content.

const DOMAIN_UK = 'https://durabledesigns.co.uk';
const DOMAIN_ASIA = 'https://durabledesigns.asia';

// Runtime origin — SSR safe (fallback to UK during build/prerender).
const getOrigin = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    // Only trust origins we own. If the site is hit via the preview URL or
    // any unknown host, we still want canonical/hreflang to point at the
    // production domains, not the preview host.
    const host = window.location.hostname;
    if (host === 'durabledesigns.asia') return DOMAIN_ASIA;
    if (host === 'durabledesigns.co.uk' || host === 'www.durabledesigns.co.uk') return DOMAIN_UK;
  }
  return DOMAIN_UK;
};

const defaultSEO = {
  title: 'Durable Designs',
  description: 'A multidisciplinary consultancy combining material science, techno-economic analysis, and intelligent digital tools. Materials engineering, durability and service-life modelling, and value-for-money analysis for infrastructure.',
  keywords: 'materials engineering, durability, service-life modelling, techno-economic analysis, benefit-cost analysis, concrete durability, probabilistic modelling, infrastructure',
};

export const SEO = ({
  title,
  description,
  keywords,
  path = '',
  noIndex = false,
}) => {
  const pageTitle = title
    ? `${title} | Durable Designs`
    : defaultSEO.title;
  const pageDescription = description || defaultSEO.description;
  const pageKeywords = keywords || defaultSEO.keywords;

  const origin = getOrigin();
  const canonicalUrl = `${origin}${path}`;
  const ukUrl = `${DOMAIN_UK}${path}`;
  const asiaUrl = `${DOMAIN_ASIA}${path}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />

      {/* Canonical — points at the current domain so each domain keeps its
          own index in Google. */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternates — tells Google these two URLs are equivalent
          content targeted at different regions. x-default is the fallback
          for everyone else and points at the UK (global) domain. */}
      <link rel="alternate" hrefLang="en-gb" href={ukUrl} />
      <link rel="alternate" hrefLang="en-hk" href={asiaUrl} />
      <link rel="alternate" hrefLang="x-default" href={ukUrl} />

      {noIndex && <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />}
      {noIndex && <meta name="googlebot" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonicalUrl} />

      {/* Twitter */}
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
    </Helmet>
  );
};
